// src/pages/dashboard/UserDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth-context';
import Navbar from '../../components/marketing/Navbar';
import LessonCard from '../../components/dashboard/LessonCard';

const UserDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchLessons();
  }, [token]);

  const fetchLessons = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lessons`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        // Token expired
        logout();
        navigate('/login');
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setLessons(data.lessons || []);
      } else {
        setError(data.message || 'Kon lessen niet laden');
      }
    } catch (error) {
      console.error('Fetch lessons error:', error);
      setError('Er is een fout opgetreden bij het laden van de lessen');
    } finally {
      setIsLoading(false);
    }
  };

  // Separate purchased and available lessons
  const purchasedLessons = lessons.filter(lesson => lesson.isPurchased);
  const availableLessons = lessons.filter(lesson => !lesson.isPurchased && !lesson.comingSoon);
  const comingSoonLessons = lessons.filter(lesson => lesson.comingSoon);

  return (
    <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-light text-purple-800 mb-2">
              Welkom terug, {user?.firstName}!
            </h1>
            <p className="text-gray-600">
              Kies een training om te beginnen of verder te gaan met leren
            </p>
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
              {error}
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
            </div>
          )}

          {/* Purchased Lessons Section */}
          {!isLoading && purchasedLessons.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl font-light text-gray-800 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Jouw Trainingen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {purchasedLessons.map(lesson => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    isPurchased={true}
                    isLocked={false}
                  />
                ))}
              </div>
            </section>
          )}

          {/* No Purchased Lessons Message */}
          {!isLoading && purchasedLessons.length === 0 && availableLessons.length > 0 && (
            <div className="mb-8 p-6 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">
                Je hebt nog geen trainingen gekocht
              </h3>
              <p className="text-purple-800">
                Kies hieronder een training om te beginnen met leren!
              </p>
            </div>
          )}

          {/* Available Lessons Section */}
          {!isLoading && availableLessons.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl font-light text-gray-800 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Beschikbare Trainingen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableLessons.map(lesson => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    isPurchased={false}
                    isLocked={lesson.isLocked}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Coming Soon Lessons Section */}
          {!isLoading && comingSoonLessons.length > 0 && (
            <section>
              <h2 className="text-2xl font-light text-gray-800 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Binnenkort Beschikbaar
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {comingSoonLessons.map(lesson => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    isPurchased={false}
                    isLocked={true}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Empty State */}
          {!isLoading && lessons.length === 0 && !error && (
            <div className="text-center py-20">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="text-xl text-gray-600 mb-2">Geen trainingen beschikbaar</h3>
              <p className="text-gray-500">Check later voor nieuwe trainingen!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;